import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import { buildImgSrc } from '@/helpers/image-url'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Icon, Image, tk, Typography } from '@sunwebgroup/ui-components'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { Detail } from './Detail'

const Wrapper = styled.div`
  position: relative;
`

const ImagesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  height: 100%;
`

const ImageWrapper = styled.button`
  overflow: hidden;
  align-content: center;
  margin-right: ${tk('spacing_xs')};
  margin-bottom: ${tk('spacing_2xs')};

  @media ${tk('mq_tablet')} {
    flex: unset;
  }
`

const StyledImage = styled(Image.Root)`
  object-fit: cover;
  height: 90px;
  width: 90px;
`
interface GalleryProps {
  name: string
  images: string[]
  analyticsEventBuilder: (index: number, name: string) => GA4AnalyticsEvent
}

export function Gallery({ name, images, analyticsEventBuilder }: GalleryProps) {
  const intl = useIntl()

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showDetail, setShowDetail] = useState(false)

  const { sendEvent } = useAnalytics()

  const handleImageClick = (index: number) => {
    setShowDetail(true)
    setSelectedIndex(index)
    sendEvent(analyticsEventBuilder(index, name))
  }

  const closeDetail = () => {
    setShowDetail(false)
  }

  return (
    <Wrapper>
      <ImagesContainer>
        {images.map((image, index) => (
          <ImageWrapper key={index} onClick={() => handleImageClick(index)}>
            <StyledImage
              defaultSource={{
                src: buildImgSrc(image, 480, 270),
                width: 480,
                height: 270,
              }}
              loaderSource={{
                src: buildImgSrc(image, 20),
              }}
              sources={[
                {
                  src: buildImgSrc(image, 240, 240),
                  width: 160,
                  height: 120,
                  breakpoint: 'breakpoint_tablet',
                },
              ]}
              alternativeText={name || ''}
            >
              <Image.Error>
                <Icon glyph="IconNoImage" size="lg" />
                <Typography $variant="body-md-bold">{intl.formatMessage({ id: 'roomtypes_image_error' })}</Typography>
              </Image.Error>
            </StyledImage>
          </ImageWrapper>
        ))}
      </ImagesContainer>
      {images?.length > 0 && (
        <Detail
          onClose={closeDetail}
          open={showDetail}
          startFromIndex={selectedIndex}
          roomtypeLabel={name}
          roomtypeImages={images}
        />
      )}
    </Wrapper>
  )
}
